<template>
  <div class="d-flex flex-column-fluid upcoming-repayments-page">
    <div class="container">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Outstanding Repayments & Fees</h1>
            <p class="subtitle" v-if="businessId">Track and manage outstanding repayment & fees schedules for this business</p>
            <p class="subtitle" v-else>Track and manage all outstanding repayment & fees schedules</p>
          </div>
          <div class="header-actions">
            <router-link 
              :to="{ name: 'business-insights', params: { business_id: businessId } }" 
              class="btn btn-dark btn-dashboard"
              v-if="businessId"
            >
              <i class="flaticon2-line-chart"></i>
              Back to Business Insights
            </router-link>
            <router-link 
              :to="{ name: 'financing-analytics' }" 
              class="btn btn-dark btn-dashboard"
              v-else
            >
              <i class="flaticon2-line-chart"></i>
              Back to Analytics Dashboard
            </router-link>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading repayments...</div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="alert alert-danger">
          <i class="material-icons">error</i>
          {{ error }}
        </div>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Search Section -->
        <div class="search-section mb-4">
          <div class="search-wrapper">
            <div class="search-input-wrapper">
              <input 
                type="text" 
                class="form-control search-input" 
                placeholder="Search by business name or invoice reference..." 
                v-model="searchTerm"
                @input="handleSearchInput"
                :disabled="false"
              />
              <span v-if="searchTerm" class="clear-icon" @click="clearSearch">
                <i class="las la-times"></i>
              </span>
              <span v-if="isSearching" class="search-loading-icon">
                <i class="las la-spinner la-spin"></i>
              </span>
            </div>
            <!-- Add Repayment Button - Only show when coming from Business Insights -->
            <div v-if="businessId" class="search-actions">
              <button 
                class="btn btn-primary add-repayment-btn" 
                @click="openAddRepaymentModal"
                :disabled="loading"
              >
                <i class="las la-plus"></i>
                Add Repayment
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          class="empty-state text-center"
          v-if="isEmpty"
        >
          <i class="las la-calendar la-5x text-muted mb-3"></i>
          <div class="empty-title">
            <span v-if="searchTerm">No repayments found for "{{ searchTerm }}"</span>
            <span v-else>No outstanding repayment & fees schedules found</span>
          </div>
          <div class="empty-subtitle" v-if="searchTerm">
            Try adjusting your search terms
          </div>
        </div>

        <!-- Repayments Table -->
        <financing-table
          v-if="!isEmpty && !error"
          :columns="tableColumns"
          :data="repayments"
          :grid-template="'200px 180px 185px 185px 185px 140px 140px'"
          :current-page="currentPage"
          :total-rows="totalRepayments"
          :page-size="pageSize"
          :table-name="'upcoming-repayments'"
          :empty-title="getEmptyTitle()"
          :empty-subtitle="searchTerm ? 'Try adjusting your search terms' : ''"
          :empty-icon="'las la-calendar'"
          :row-class="getRowClass"
          :tooltip="getRepaymentTooltip"
          @page-change="setPage"
        />
      </div>
    </div>

    <!-- Add Repayment Modal -->
    <AddRepaymentModal 
      v-if="businessId"
      ref="addRepaymentModal" 
      :businessId="businessId" 
      :currency="currency"
      @repayment-added="onRepaymentAdded" 
    />
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import BasePagination from "@/components/UI/BasePagination.vue";
import FinancingTable from "@/components/UI/FinancingTable.vue";
import AddRepaymentModal from "./components/AddRepaymentModal.vue";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "UpcomingRepayments",
  components: {
    BasePagination,
    FinancingTable,
    AddRepaymentModal
  },
  data() {
    return {
      loading: true,
      error: null,
      
      // Repayments data
      repayments: [],
      totalRepayments: 0,
      currentPage: 1,
      pageSize: 20,
      currency: 'USD',
      businessId: null,
      
      // Search
      searchTerm: "",
      debounceTimer: null,
      isSearching: false,
    };
  },
  
  computed: {
    isEmpty() {
      return this.repayments.length === 0;
    },
    
    tableColumns() {
      return [
        {
          header: 'Business Name',
          field: 'business_name',
          type: 'text',
          align: 'text-center',
          class: 'business-name-col',
          icon: 'las la-building'
        },
        {
          header: 'Invoice Number',
          field: 'invoice_reference',
          type: 'text',
          align: 'text-center',
          class: 'invoice-reference-col',
          icon: 'las la-file-invoice'
        },
        {
          header: 'Total Disbursed Amount',
          field: 'disbursement_amount',
          type: 'currency',
          align: 'text-right',
          class: 'disbursed-amount-col',
          icon: 'las la-money-bill',
          currencyField: 'currency'
        },
        {
          header: 'Total Fees',
          field: 'total_fees',
          type: 'currency',
          align: 'text-right',
          class: 'transaction-fee-col',
          icon: 'las la-percentage',
          defaultCurrency: 'AED'
        },
        {
          header: 'AMOUNT DUE',
          field: 'repayment_amount',
          type: 'currency',
          align: 'text-right',
          class: 'amount-col',
          icon: 'las la-money-bill',
          currencyField: 'currency'
        },
        {
          header: 'Due Date',
          field: 'due_date',
          type: 'date',
          align: 'text-center',
          class: 'due-date-col',
          icon: 'las la-calendar',
          showBadge: true
        },
        {
          header: 'Status',
          field: 'status',
          type: 'status',
          align: 'text-center',
          class: 'status-col',
          icon: 'las la-toggle-on'
        },
      ];
    }
  },
  
  mounted() {
    // Set business ID from route
    this.businessId = this.$route.params.business_id || this.$route.query.business_id;
    this.loadRepayments();
  },
  
  beforeDestroy() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  
  methods: {
    async loadRepayments(page = 1) {
      try {
        // Only show main loading if it's not a search operation
        if (!this.isSearching) {
          this.loading = true;
        }
        this.error = null;
        
        const params = {
          page,
          page_size: this.pageSize,
        };
        
        if (this.searchTerm) {
          params.search = this.searchTerm;
        }
        
        // Use stored business ID (or get from route if not set)
        if (!this.businessId) {
          this.businessId = this.$route.params.business_id || this.$route.query.business_id;
        }
        
        // If businessId is provided, use getRepayments, otherwise use getAdminRepayments
        let response;
        if (this.businessId) {
          response = await FinancingService.getRepayments(this.businessId, params);
        } else {
          response = await FinancingService.getAdminRepayments(params);
        }
        if (response && response.data) {
          this.repayments = this.formatRepaymentData(response.data);
          this.totalRepayments = response.count || response.total || response.data.length || 0;
          this.currentPage = page;
          if (response.data.length > 0) {
            this.currency = response.data[0].currency || 'USD';
          }
        } else {
          this.repayments = [];
          this.totalRepayments = 0;
        }
        
      } catch (error) {
        console.error('Error loading repayments:', error);
        this.error = formatErrorMessage(error.message || 'Failed to load repayments');
        this.repayments = [];
        this.totalRepayments = 0;
      } finally {
        if (!this.isSearching) {
          this.loading = false;
        }
      }
    },
    
    formatRepaymentData(repayments) {
      return repayments.map(repayment => ({
        id: repayment.id || repayment.repayment_id || repayment.finance_request_id,
        business_name: repayment.borrower_name || repayment.business_name || repayment.borrower_display_name || "Unknown Business",
        invoice_reference: repayment.invoice_reference || repayment.reference || repayment.finance_request_reference || "N/A",
        repayment_amount: repayment.amount_due || repayment.repayment_amount || repayment.amount || repayment.total_amount || 0,
        due_date: repayment.due_date || repayment.due_at || repayment.repayment_date,
        status: repayment.due_status || repayment.status || repayment.repayment_status || "DUE",
        currency: repayment.currency || repayment.base_currency || "USD",
        disbursement_amount: repayment.disbursement_amount || repayment.total_disbursed_amount || 0,
        total_fees: repayment.total_fees || repayment.total_transaction_fees || 0,
        // Raw data for potential future use
        raw: repayment,
      }));
    },
    
    handleSearchInput() {
      // Clear existing timer
      clearTimeout(this.debounceTimer);
      
      // Set new timer for debounced search
      this.debounceTimer = setTimeout(async () => {
        this.currentPage = 1;
        this.isSearching = true;
        await this.loadRepayments(1);
        this.isSearching = false;
      }, 500); // Increased debounce time for better UX
    },
    
    clearSearch() {
      this.searchTerm = "";
      this.currentPage = 1;
      this.isSearching = true;
      this.loadRepayments(1).finally(() => {
        this.isSearching = false;
      });
    },
    
    setPage(page, pageSize) {
      // If pageSize is provided and different from current, update it
      if (pageSize && pageSize !== this.pageSize) {
        this.pageSize = pageSize;
      }
      
      // Fetch repayments for the requested page
      if (page >= 1) {
        this.loadRepayments(page);
      }
    },
    
    formatCurrency(amount) {
      if (!amount || isNaN(amount)) return this.$formatCurrency(0, this.currency);
      return this.$formatCurrency(amount, this.currency);
    },
    
    formatDate(dateString) {
      if (!dateString) return 'No due date';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatStatus(status) {
      const statusMap = {
        'DUE': 'Due',
        'OVERDUE': 'Overdue',
        'PAID': 'Paid'
      };
      return statusMap[status] || status;
    },
    
    getStatusClass(status) {
      const classMap = {
        'DUE': 'status-due',
        'OVERDUE': 'status-overdue',
        'PAID': 'status-paid'
      };
      return classMap[status] || 'status-default';
    },
    
    isOverdue(dueDate) {
      if (!dueDate) return false;
      const due = new Date(dueDate);
      const today = new Date();
      return due < today;
    },
    
    isUpcoming(dueDate) {
      if (!dueDate) return false;
      const due = new Date(dueDate);
      const today = new Date();
      const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      return due >= today && due <= nextWeek;
    },
    
    getRepaymentTooltip(repayment) {
      if (this.isOverdue(repayment.due_date)) {
        return `Overdue by ${this.getDaysOverdue(repayment.due_date)} days`;
      } else if (this.isUpcoming(repayment.due_date)) {
        return `Due in ${this.getDaysUntilDue(repayment.due_date)} days`;
      }
      return null;
    },
    
    getDaysOverdue(dueDate) {
      if (!dueDate) return 0;
      const due = new Date(dueDate);
      const today = new Date();
      const diffTime = today - due;
      return diffTime / (1000 * 60 * 60 * 24);
    },
    
    getDaysUntilDue(dueDate) {
      if (!dueDate) return 0;
      const due = new Date(dueDate);
      const today = new Date();
      const diffTime = due - today;
      return diffTime / (1000 * 60 * 60 * 24);
    },
    
    openAddRepaymentModal() {
      if (this.$refs.addRepaymentModal) {
        this.$refs.addRepaymentModal.open();
      }
    },
    
    async onRepaymentAdded() {
      // Refresh the repayments list after a repayment is added
      await this.loadRepayments(this.currentPage);
    },
    
    getRowClass(repayment) {
      let classes = [];
      if (this.isOverdue(repayment.due_date)) {
        classes.push('overdue');
      } else if (this.isUpcoming(repayment.due_date)) {
        classes.push('upcoming');
      }
      return classes.join(' ');
    },
    
    getEmptyTitle() {
      return this.searchTerm ? `No repayments found for "${this.searchTerm}"` : 'No outstanding repayment & fees schedules found';
    },
  },
};
</script>

<style scoped lang="scss">
@import './index.scss';

// Additional styles specific to outstanding repayment & fees schedules
.upcoming-repayments-page {
  .search-section {
    .search-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      
      .search-input-wrapper {
        position: relative;
        max-width: 400px;
        flex: 1;
        
        .search-input {
          padding: 0.6rem 2.5rem 0.6rem 1rem;
          border: 1px solid #E5E5E5;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          
          &:focus {
            border-color: #1e293b;
            box-shadow: 0 0 0 0.2rem rgba(30, 41, 59, 0.1);
          }
        }
        
        .clear-icon {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          color: #6c757d;
          font-size: 1.1rem;
          
          &:hover {
            color: #1e293b;
          }
        }
        
        .search-loading-icon {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #1e293b;
          font-size: 1rem;
          pointer-events: none;
          
          i {
            animation: spin 1s linear infinite;
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      }
      
      .search-actions {
        display: flex;
        align-items: center;
        
        .add-repayment-btn {
          background-color: #1e293b;
          border-color: #1e293b;
          color: white;
          padding: 0.6rem 1.25rem;
          border-radius: 0.375rem;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          white-space: nowrap;
          
          &:hover:not(:disabled) {
            background-color: #0f172a;
            border-color: #0f172a;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(30, 41, 59, 0.3);
          }
          
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          
          i {
            margin-right: 0.5rem;
            font-size: 1rem;
          }
        }
      }
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .search-section {
    .search-wrapper {
      flex-direction: column;
      align-items: stretch;
      gap: 0.75rem;
      
      .search-input-wrapper {
        max-width: none;
      }
      
      .search-actions {
        justify-content: center;
        
        .add-repayment-btn {
          width: 100%;
          justify-content: center;
        }
      }
    }
  }
}
</style> 